import React, { useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import Facebook from './Facebook';

const DetailedView = ({ link, onBack }) => {
  const [clicksOverTime, setClicksOverTime] = useState([]);
  const [uniqueVisitorsOverTime, setUniqueVisitorsOverTime] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [browserData, setBrowserData] = useState([]);

  useEffect(() => {
    if (link.clickTimestamps) {
      const formattedClicks = link.clickTimestamps.map((entry) => ({
        timestamp: new Date(entry.timestamp),
        value: 1,
      }));
      setClicksOverTime(formattedClicks);
    }

    if (link.uniqueVisitors) {
      const formattedVisitors = link.uniqueVisitors.map((visitor) => ({
        timestamp: new Date(visitor.timestamp),
        value: 1,
      }));
      setUniqueVisitorsOverTime(formattedVisitors);

      // Group visitors by country
      const countryCounts = link.uniqueVisitors.reduce((acc, visitor) => {
        const country = visitor.country || 'Unknown';
        acc[country] = (acc[country] || 0) + 1;
        return acc;
      }, {});
      setCountryData(
        Object.entries(countryCounts).map(([country, count]) => ({
          country,
          count,
        }))
      );

      // Group visitors by device
      const deviceCounts = link.uniqueVisitors.reduce((acc, visitor) => {
        const device = visitor.device || 'Unknown';
        acc[device] = (acc[device] || 0) + 1;
        return acc;
      }, {});
      setDeviceData(
        Object.entries(deviceCounts).map(([device, count]) => ({
          device,
          count,
        }))
      );

      // Group visitors by browser
      const browserCounts = link.uniqueVisitors.reduce((acc, visitor) => {
        const browser = visitor.browser || 'Unknown';
        acc[browser] = (acc[browser] || 0) + 1;
        return acc;
      }, {});
      setBrowserData(
        Object.entries(browserCounts).map(([browser, count]) => ({
          browser,
          count,
        }))
      );
    }
  }, [link]);

  const renderChart = (canvasId, data, label, color, type = 'bar') => {
    const ctx = document.getElementById(canvasId).getContext('2d');
    new Chart(ctx, {
      type,
      data: {
        labels: data.map((entry) => entry.timestamp || entry.country || entry.device || entry.browser),
        datasets: [
          {
            label: label,
            data: data.map((entry) => entry.value || entry.count),
            backgroundColor: color,
            borderColor: color,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
        },
        scales: {
          x: {
            type: type === 'line' ? 'time' : undefined,
            time: {
              unit: 'day',
              displayFormats: { day: 'MMM d' },
            },
          },
          y: {
            beginAtZero: true,
          },
        },
      },
    });
  };

  useEffect(() => {
    if (clicksOverTime.length > 0) {
      renderChart('clicksOverTimeChart', clicksOverTime, 'Clicks Over Time', '#4CAF50', 'line');
    }
    if (uniqueVisitorsOverTime.length > 0) {
      renderChart('uniqueVisitorsChart', uniqueVisitorsOverTime, 'Unique Visitors Over Time', '#36A2EB', 'line');
    }
    if (countryData.length > 0) {
      renderChart('countryStatsChart', countryData, 'Visitors by Country', '#FFCE56');
    }
    if (deviceData.length > 0) {
      renderChart('deviceStatsChart', deviceData, 'Visitors by Device', '#FFA500');
    }
    if (browserData.length > 0) {
      renderChart('browserStatsChart', browserData, 'Visitors by Browser', '#8A2BE2');
    }
  }, [clicksOverTime, uniqueVisitorsOverTime, countryData, deviceData, browserData]);

  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      <button
        onClick={onBack}
        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mb-4"
      >
        &lt; Back to Links
      </button>

      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-2xl font-bold mb-4">
          Details for{' '}
          <a
            href={`${window.location.origin}/${link.shortUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline hover:text-blue-700"
          >
            {`${window.location.origin}/${link.shortUrl}`}
          </a>
        </h2>
        <p className="mb-2">
          <strong>Long URL:</strong>{' '}
          <a
            href={link.longUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline hover:text-blue-700"
          >
            {link.longUrl}
          </a>
        </p>
        <p className="mb-2">
          <strong>Clicks:</strong> {link.clicks}
        </p>
        <p>
          <strong>Created:</strong>{' '}
          {new Date(link.createdAt).toLocaleDateString()}
        </p>
      </div>
      {/* <Facebook /> */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-4">Clicks Over Time</h3>
          <canvas id="clicksOverTimeChart"></canvas>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-4">Unique Visitors Over Time</h3>
          <canvas id="uniqueVisitorsChart"></canvas>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-4">Visitors by Country</h3>
          <canvas id="countryStatsChart"></canvas>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-4">Visitors by Device</h3>
          <canvas id="deviceStatsChart"></canvas>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-bold mb-4">Visitors by Browser</h3>
          <canvas id="browserStatsChart"></canvas>
        </div>
      </div>
    </div>
  );
};

export default DetailedView;
